import React, { useEffect, useRef, useState } from 'react';

interface DesmosEmbedProps {
  type?: 'graphing' | 'scientific' | 'four-function';
  expressions?: string[];
  readOnly?: boolean;
  height?: number;
}

declare global {
  interface Window {
    Desmos: any;
  }
}

const DESMOS_API_KEY = process.env.REACT_APP_DESMOS_API_KEY || '';
let scriptLoading = false;
let scriptLoaded = false;
const loadCallbacks: (() => void)[] = [];

function loadDesmosScript(): Promise<void> {
  if (scriptLoaded && window.Desmos) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    loadCallbacks.push(resolve);

    if (scriptLoading) return;
    scriptLoading = true;

    const script = document.createElement('script');
    script.src = `https://www.desmos.com/api/v1.9/calculator.js?apiKey=${DESMOS_API_KEY}`;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    script.onerror = () => {
      scriptLoading = false;
      loadCallbacks.forEach((cb) => cb());
      loadCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
}

export const DesmosEmbed: React.FC<DesmosEmbedProps> = ({
  type = 'graphing',
  expressions = [],
  readOnly = true,
  height = 300,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const calculatorRef = useRef<any>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!DESMOS_API_KEY) {
      setError(true);
      return;
    }

    let mounted = true;

    loadDesmosScript().then(() => {
      if (!mounted || !containerRef.current || !window.Desmos) {
        if (mounted) setError(true);
        return;
      }

      try {
        const options: any = {
          expressionsCollapsed: true,
          settingsMenu: false,
          zoomButtons: !readOnly,
          expressions: !readOnly,
          lockViewport: readOnly,
          border: false,
        };

        let calc: any;
        if (type === 'scientific') {
          calc = window.Desmos.ScientificCalculator(containerRef.current, options);
        } else if (type === 'four-function') {
          calc = window.Desmos.FourFunctionCalculator(containerRef.current, options);
        } else {
          calc = window.Desmos.GraphingCalculator(containerRef.current, options);
        }

        calculatorRef.current = calc;

        // Add expressions
        expressions.forEach((expr, i) => {
          calc.setExpression({ id: `expr_${i}`, latex: expr });
        });
      } catch {
        setError(true);
      }
    });

    return () => {
      mounted = false;
      if (calculatorRef.current) {
        try {
          calculatorRef.current.destroy();
        } catch {}
        calculatorRef.current = null;
      }
    };
  }, [type, readOnly, JSON.stringify(expressions)]);

  if (error || !DESMOS_API_KEY) {
    return (
      <div className="desmos-fallback">
        <p>Interactive graph unavailable.</p>
        {expressions.length > 0 && (
          <p className="desmos-fallback-expr">
            {expressions.map((e, i) => (
              <code key={i}>{e}</code>
            ))}
          </p>
        )}
        <a
          href={`https://www.desmos.com/calculator`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Desmos
        </a>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="desmos-container"
      style={{ width: '100%', height: `${height}px` }}
    />
  );
};
